import type { TFunction } from 'i18next';
import { Lightbulb, LightbulbOff, MapPin } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { FieldRow } from '@ooh/ui/components/field-row';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import {
  formatArea,
  formatCoordinatePair,
  formatDimensions,
  capitalize,
} from '@ooh/ui/lib/media-unit-formatters';
import { getMediaTypeLabel } from '../../lib/map-config';
import type { DetailViewUnit } from './detail-view-unit';

/**
 * Sidebar cards for `DetailSidebar` (the cost and notes cards live in
 * `detail-sidebar-cost-cards.tsx`) — split out to stay within the line budgets.
 * Not meant for reuse outside `DetailSidebar`.
 */

// ─── Presence helpers ─────────────────────────────────────────
//
// Presence is keyed on KEY presence, not value: the client payload omits
// internal-only sections entirely (key absent), whereas the full internal
// payload always carries the key (value may be null). This keeps the internal
// catalog behavior unchanged (e.g. notes-card still shows its "no notes"
// placeholder when notes is null) while omitting the section for clients.

/** True when the unit carries the internal cost keys (full internal payload). */
export function hasCostSection(unit: DetailViewUnit): boolean {
  return 'catalogPrice' in unit;
}

/** True when the unit carries the supplier keys (internal-only section). */
export function hasSupplierSection(unit: DetailViewUnit): boolean {
  return 'supplierName' in unit || 'supplierNumber' in unit;
}

/** True when the unit carries the notes key (internal-only section). */
export function hasNotesSection(unit: DetailViewUnit): boolean {
  return 'notes' in unit;
}

export function SpecsCard({
  unit,
  t,
  tCommon,
  tMap,
}: {
  unit: DetailViewUnit;
  t: TFunction<'inventory', undefined>;
  tCommon: TFunction<'common', undefined>;
  tMap: TFunction<'map', undefined>;
}) {
  const lit = unit.illuminated ?? false;
  return (
    <Card data-testid="spec-card">
      <CardHeader className="pb-1 pt-4">
        <CardTitle className="text-[13px] uppercase tracking-wide text-muted-foreground font-semibold">
          {t('detail.sections.specs')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        <div className="divide-y divide-border/40">
          <FieldRow testId="field-format" label={t('detail.fields.mediaType')}>
            {unit.mediaType ? getMediaTypeLabel(unit.mediaType, tMap) : '\u2014'}
          </FieldRow>
          <FieldRow testId="field-dimensions" label={t('detail.fields.dimensions')}>
            <span className="font-mono text-xs">{formatDimensions(unit.widthM, unit.heightM)}</span>
          </FieldRow>
          <FieldRow testId="field-area" label={t('detail.fields.area')}>
            <span className="font-mono text-xs">{formatArea(unit.areaSqm)}</span>
          </FieldRow>
          <FieldRow testId="field-material" label={t('detail.fields.material')}>
            {unit.material ?? '\u2014'}
          </FieldRow>
          <FieldRow testId="field-lighting" label={t('detail.fields.lighting')}>
            <span className="flex items-center gap-1.5">
              {lit ? (
                <>
                  <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-emerald-600 font-medium text-xs">
                    {tCommon('bool.yes')}
                  </span>
                </>
              ) : (
                <>
                  <LightbulbOff className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground text-xs">{tCommon('bool.no')}</span>
                </>
              )}
            </span>
          </FieldRow>
        </div>
      </CardContent>
    </Card>
  );
}

export function SupplierCard({
  unit,
  t,
  maskedExplanation,
}: {
  unit: DetailViewUnit;
  t: TFunction<'inventory', undefined>;
  maskedExplanation: string;
}) {
  const supplierWithheld = unit.supplierWithheld ?? false;
  return (
    <Card data-testid="supplier-card">
      <CardHeader className="pb-1 pt-4">
        <CardTitle className="text-[13px] uppercase tracking-wide text-muted-foreground font-semibold">
          {t('detail.sections.supplier')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        <div className="divide-y divide-border/40">
          <FieldRow testId="field-supplier" label={t('detail.fields.supplier')}>
            {maskFieldValue(
              unit.supplierName,
              supplierWithheld,
              maskedExplanation,
              unit.supplierName ?? '\u2014',
            )}
          </FieldRow>
          <FieldRow testId="field-supplierNumber" label={t('detail.fields.supplierNumber')}>
            {maskFieldValue(
              unit.supplierNumber,
              supplierWithheld,
              maskedExplanation,
              <span className="font-mono text-xs text-muted-foreground">
                {unit.supplierNumber ?? '\u2014'}
              </span>,
            )}
          </FieldRow>
        </div>
      </CardContent>
    </Card>
  );
}

export function LocationCard({
  unit,
  t,
  clientView = false,
}: {
  unit: DetailViewUnit;
  t: TFunction<'inventory', undefined>;
  /** Suppresses the Google Maps button — see MediaUnitDetailView's `clientView` doc. */
  clientView?: boolean;
}) {
  return (
    <Card data-testid="location-card">
      <CardHeader className="pb-1 pt-4">
        <CardTitle className="text-[13px] uppercase tracking-wide text-muted-foreground font-semibold">
          {t('detail.sections.location')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        <div className="divide-y divide-border/40">
          <FieldRow testId="field-region" label={t('detail.fields.region')}>
            {capitalize(unit.region)}
          </FieldRow>
          <FieldRow testId="field-county" label={t('detail.fields.county')}>
            {capitalize(unit.county)}
          </FieldRow>
          <FieldRow testId="field-agglomeration" label={t('detail.fields.agglomeration')}>
            {unit.agglomeration ?? '\u2014'}
          </FieldRow>
          <FieldRow testId="field-city" label={t('detail.fields.city')}>
            {unit.city}
          </FieldRow>
          <FieldRow testId="field-address" label={t('detail.fields.address')}>
            {unit.address ?? '\u2014'}
          </FieldRow>
          <FieldRow testId="field-coordinates" label={t('detail.fields.gpsCoordinates')}>
            {unit.latitude != null && unit.longitude != null ? (
              <span className="font-mono text-2xs">
                {formatCoordinatePair(unit.latitude, unit.longitude)}
              </span>
            ) : (
              '\u2014'
            )}
          </FieldRow>
        </div>
        {!clientView && unit.latitude != null && unit.longitude != null && (
          <div className="mt-3">
            <Button asChild variant="outline" size="sm" fullWidth>
              <a
                href={`https://www.google.com/maps?q=${unit.latitude},${unit.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin className="h-3.5 w-3.5" />
                {t('detail.openInGoogleMaps')}
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
